import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, fmtBRL } from "@/lib/api";
import { Calculator, CheckCircle, ArrowRight, ShieldCheck, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Highlight } from "@/components/Highlight";

const PLANS = [
  { id: "basico", name: "Básico", rate: 0.09, mult: 30 },
  { id: "plus",   name: "Fit",    rate: 0.11, mult: 40 },
  { id: "ultra",  name: "Ultra",  rate: 0.15, mult: 50 },
];

export default function SimulatorSection({ content }) {
  const c = content || {};
  const [step, setStep] = useState(1); // 1=simular, 2=lead form, 3=result
  const [rent, setRent] = useState("");
  const [plan, setPlan] = useState("plus");
  const [form, setForm] = useState({ name: "", cpf: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const rentNum = parseFloat(rent || 0);
  const selectedPlan = PLANS.find((p) => p.id === plan);
  const preview = selectedPlan && rentNum ? {
    premium: rentNum * selectedPlan.rate,
    coverage: rentNum * selectedPlan.mult,
  } : null;

  const submit = async () => {
    if (!form.name || !form.cpf || !form.email || !form.phone) {
      toast.error("Preencha todos os dados de contato"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/simulate", {
        ...form, rent_amount: rentNum, plan, origin: "landing",
      });
      setResult(data);
      setStep(3);
      toast.success("Simulação concluída!");
    } catch { toast.error("Erro na simulação"); }
    finally { setLoading(false); }
  };

  const restart = () => {
    setStep(1); setRent(""); setForm({ name: "", cpf: "", email: "", phone: "" }); setResult(null);
  };

  return (
    <section id="simulador" className="bg-white border-y border-[var(--line)]">
      <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-3 flex items-center gap-2"><Calculator size={16} weight="duotone" /> {c.eyebrow || "Simulador gratuito"}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight" data-testid="sim-title">
            <Highlight text={c.title || "Descubra em 30 segundos quanto sua fiança vai custar."} highlight={c.title_highlight ?? "30 segundos"} />
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed" data-testid="sim-subtitle">
            {c.subtitle || "Simule sem compromisso. Uma imobiliária parceira Safimob entra em contato para dar sequência ao processo de análise e ativação da garantia."}
          </p>
          <div className="mt-8 space-y-3">
            {(c.bullets || [
              "Sem fiador, sem caução, sem seguro fiança tradicional",
              "Análise de crédito em minutos",
              "Cobertura de aluguel + condomínio + IPTU",
              "Contrato 100% digital",
            ]).map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle size={16} weight="fill" color="#059669" /> {b}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="card-flat p-8 shadow-sm" data-testid="simulator-card">
            {step === 1 && (
              <>
                <div className="eyebrow mb-1">Passo 1 de 2</div>
                <div className="font-display text-2xl mb-6">Qual o valor do aluguel?</div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1.5 block">Valor mensal do aluguel</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 mono">R$</span>
                    <Input data-testid="sim-rent" type="number" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="2500" className="pl-10 h-12 text-lg" />
                  </div>
                </div>

                <div className="mt-6">
                  <Label className="text-xs text-slate-500 mb-2 block">Escolha o plano</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {PLANS.map((p) => {
                      const sel = plan === p.id;
                      return (
                        <button key={p.id} type="button" onClick={() => setPlan(p.id)} data-testid={`sim-plan-${p.id}`}
                          className={`p-3 rounded-md border-2 text-left transition-all ${sel ? "border-[var(--emerald)] bg-[var(--emerald-soft)]" : "border-[var(--line)] hover:border-slate-300"}`}>
                          <div className="font-display text-lg">{p.name}</div>
                          <div className="text-[10px] text-slate-500">{(p.rate*100).toFixed(0)}% · até {p.mult}x</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {preview && (
                  <div className="mt-6 p-5 rounded-md bg-[var(--emerald)] text-white" data-testid="sim-preview">
                    <div className="eyebrow text-white/60">Valor estimado do prêmio</div>
                    <div className="metric-value text-4xl mt-1">{fmtBRL(preview.premium)}<span className="text-sm text-white/60 ml-1">/mês</span></div>
                    <div className="text-xs text-white/80 mt-2">Cobertura total até <strong className="mono">{fmtBRL(preview.coverage)}</strong> ({selectedPlan.mult}x o aluguel)</div>
                  </div>
                )}

                <Button data-testid="sim-next" disabled={!rentNum} onClick={() => setStep(2)} className="btn-primary w-full mt-6 h-12">
                  Continuar <ArrowRight size={16} className="ml-2" />
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="eyebrow mb-1">Passo 2 de 2</div>
                <div className="font-display text-2xl mb-2">Seus dados de contato</div>
                <div className="text-sm text-slate-500 mb-6">Uma imobiliária parceira entrará em contato para dar sequência.</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label className="text-xs text-slate-500 mb-1.5 block">Nome completo</Label><Input data-testid="sim-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                  <div><Label className="text-xs text-slate-500 mb-1.5 block">CPF</Label><Input data-testid="sim-cpf" value={form.cpf} onChange={(e) => setForm({...form, cpf: e.target.value})} placeholder="000.000.000-00" /></div>
                  <div><Label className="text-xs text-slate-500 mb-1.5 block">Telefone/WhatsApp</Label><Input data-testid="sim-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="(11) 98765-4321" /></div>
                  <div className="col-span-2"><Label className="text-xs text-slate-500 mb-1.5 block">Email</Label><Input data-testid="sim-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Voltar</Button>
                  <Button data-testid="sim-submit" onClick={submit} disabled={loading} className="btn-primary flex-1 h-12">
                    <ShieldCheck size={16} className="mr-2" /> {loading ? "Enviando…" : "Ver resultado"}
                  </Button>
                </div>
              </>
            )}

            {step === 3 && result && (
              <div className="text-center" data-testid="sim-result">
                <div className="w-16 h-16 rounded-full bg-[var(--success-soft)] flex items-center justify-center mx-auto">
                  <CheckCircle size={34} weight="fill" color="#059669" />
                </div>
                <div className="font-display text-3xl mt-4">Simulação pronta!</div>
                <div className="text-slate-600 mt-2 text-sm">Plano <strong>{result.plan_label}</strong> — {result.rate_percent}% do aluguel</div>

                <div className="grid grid-cols-2 gap-3 mt-6">
                  <div className="p-4 rounded-md bg-[var(--emerald)] text-white">
                    <div className="eyebrow text-white/60">Prêmio mensal</div>
                    <div className="metric-value text-3xl mt-1">{fmtBRL(result.monthly_premium)}</div>
                  </div>
                  <div className="p-4 rounded-md bg-slate-50 border border-slate-100">
                    <div className="eyebrow">Cobertura total</div>
                    <div className="metric-value text-3xl mt-1 text-[var(--emerald)]">{fmtBRL(result.coverage_total)}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{result.coverage_multiplier}x o aluguel</div>
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-md bg-slate-50 border border-slate-100 text-sm text-slate-700">
                  <strong>Próximo passo:</strong> {result.message}
                </div>

                <Button onClick={restart} variant="outline" className="mt-6" data-testid="sim-new">Fazer outra simulação</Button>
              </div>
            )}

            <div className="mt-6 text-[11px] text-slate-400 text-center leading-relaxed">
              Ao simular você concorda em receber contato da Safimob e das imobiliárias parceiras.
              Seus dados são tratados conforme a LGPD.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
